import React from "react";

import { useNextDevServer } from "@/features/code-execution/context";
import { NextDevServerStatus } from "@/features/code-execution/useNextDevServerHook";
import { Progress } from "@replo/design-system/components/shadcn/core/progress";
import twMerge from "@replo/design-system/utils/twMerge";
import { useNProgress } from "@tanem/react-nprogress";
import { BadgeCheckIcon, LoaderIcon } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

const mapStatusToProgress = (status: NextDevServerStatus) => {
  return exhaustiveSwitch({ type: status.toString() })({
    [NextDevServerStatus.Booting.toString()]: 0,
    [NextDevServerStatus.WaitingForFiles.toString()]: 5,
    [NextDevServerStatus.Mounting.toString()]: 15,
    [NextDevServerStatus.Installing.toString()]: 30,
    [NextDevServerStatus.Starting.toString()]: 40,
    [NextDevServerStatus.Downloading.toString()]: 50,
    [NextDevServerStatus.Ready.toString()]: 60,
    [NextDevServerStatus.Compiling.toString()]: 75,
    [NextDevServerStatus.Serving.toString()]: 100,
    [NextDevServerStatus.Error.toString()]: 100,
  });
};

// Linear Interpolation
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const useAnimatedProgress = (target: number, baseSpeed = 0.02) => {
  const [value, setValue] = React.useState(target);
  const maxProgressRef = React.useRef(target);
  const targetStabilizedAtRef = React.useRef<{
    target: number;
    time: number;
  } | null>(null);

  React.useEffect(() => {
    // Update max progress - never allow it to decrease
    maxProgressRef.current = Math.max(maxProgressRef.current, target);

    // Track when target becomes stable at a specific value
    if (
      !targetStabilizedAtRef.current ||
      targetStabilizedAtRef.current.target !== target
    ) {
      targetStabilizedAtRef.current = { target, time: Date.now() };
    }

    let frame: number;

    const animate = () => {
      setValue((v) => {
        let effectiveTarget = maxProgressRef.current;

        // If we've been stuck at 75% for more than 1 second, continue visual progress to 95%
        if (targetStabilizedAtRef.current && target === 75) {
          const timeStuck = Date.now() - targetStabilizedAtRef.current.time;
          if (timeStuck > 1000) {
            // After 1 second stuck at 75%, progress towards 95% over 8 seconds
            const progressTime = Math.min(timeStuck - 1000, 8000);
            const progressRatio = progressTime / 8000;
            // Ease out function for smoother deceleration
            const easedRatio = 1 - Math.pow(1 - progressRatio, 3);
            const visualTarget = 75 + (95 - 75) * easedRatio;
            effectiveTarget = Math.max(effectiveTarget, visualTarget);
            maxProgressRef.current = effectiveTarget;
          }
        }

        // ---------- speed logic ----------
        // 1. Normal: creep at baseSpeed (e.g. 2 % of gap per frame)
        // 2. Finish: if target is 100, bump speed way up
        //            (feel free to fine-tune 0.18 → 0.25 etc.)
        const speed = target === 100 ? 0.18 : baseSpeed;

        const next = lerp(v, effectiveTarget, speed);

        // snap when we're <0.5 % away to avoid interminable micro-steps
        if (Math.abs(next - effectiveTarget) < 0.5) {
          return effectiveTarget;
        }

        frame = requestAnimationFrame(animate);
        return next;
      });
    };

    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [target, baseSpeed]);

  return value;
};

// inside your component:

const LoadingHeader: React.FC = () => {
  const { status, mountAdditionalFilesStatus } = useNextDevServer();

  const isServing = status === NextDevServerStatus.Serving;
  const isFullyLoaded =
    isServing &&
    ["compiled", "notStarted"].includes(mountAdditionalFilesStatus);
  const { progress: simulatedProgressForIncrementalUpdates } = useNProgress({
    isAnimating: isServing && !isFullyLoaded,
    incrementDuration: 5,
  });
  const [isHidden, setIsHidden] = useOverridableState(isFullyLoaded);
  const realProgressForStartingServer = useAnimatedProgress(
    mapStatusToProgress(status),
  );

  // Note (Noah, 2025-09-11): If we're already serving, that means we should use
  // simulated progress for any incremental updates. Otherwise we're still
  // starting up the dev server, so we use actual progress based on the dev
  // server logs
  const progress = isServing
    ? simulatedProgressForIncrementalUpdates * 100
    : realProgressForStartingServer;

  React.useEffect(() => {
    if (isFullyLoaded) {
      const timeout = setTimeout(() => {
        setIsHidden(true);
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [isFullyLoaded, setIsHidden]);

  return (
    <div
      className={twMerge(
        "absolute top-0 left-0 right-0 overflow-hidden z-1",
        isHidden && "pointer-events-none",
      )}
    >
      <div
        className={twMerge(
          "bg-white flex flex-col items-stretch gap-3",
          "transition-transform duration-300",
          !isServing && "pb-3 border-b border-border",
          isHidden && "-translate-y-full",
        )}
      >
        <Progress
          value={progress}
          indicatorClassName="bg-slate-800 transition"
          className="w-auto rounded-none bg-surface"
        />
        {!isServing && (
          <div className="flex flex-row px-4 gap-2">
            <div
              className={twMerge(
                "flex flex-row items-center border-1 text-info border-info-border justify-center h-6 w-6 gap-2 bg-info-soft rounded-md",
                isFullyLoaded &&
                  "text-success bg-success-soft border-success-border",
              )}
            >
              {isFullyLoaded ? (
                <BadgeCheckIcon size={12} />
              ) : (
                <LoaderIcon
                  size={12}
                  className="animate-[spin_1.5s_linear_infinite]"
                />
              )}
            </div>
            <div>
              <div className="header-h4">
                {isFullyLoaded ? "Good to go!" : "Loading Your Page"}
              </div>
              <div className="text-base text-muted">
                {isFullyLoaded
                  ? "Your page is fully interactive and ready to edit."
                  : "You'll be able to interact with your page soon."}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoadingHeader;
